import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useCart } from '../../hooks/useCart'
import { useAuth } from '../../hooks/useAuth'
import { useBusinessRulesContext } from '../../hooks/useBusinessRulesContext'
import { useShipping } from '../../hooks/useShipping'
import { useDeliveryDistance } from '../../hooks/useDeliveryDistance'
import { createCheckoutOrder, getOrder, updateOrder } from '../../services/orders.service'
import { getCustomerProfileByEmail } from '../../services/customers.service'
import { getImageUrl } from '../../services/cloudinary.service'
import { createMercadoPagoPreference } from '../../services/mercadopago.service'
import { getProductStockSummary } from '../../services/stock.service'
import { formatCurrency, isValidEmail, normalizeCountryName } from '../../utils/helpers'
import { getStockForSelection } from '../../utils/stock'
import { notifyOrderCreated } from '../../services/notifications.service'
import Toast from '../../components/Toast'
import PaymentMethodSelector from '../../components/PaymentMethodSelector'
import FulfillmentSelector from '../../components/FulfillmentSelector'

function FashionCheckout() {
  const navigate = useNavigate()
  const { cartItems, getSubtotal, getShippingQuote, clearCart } = useCart()
  const { currentUser, userData } = useAuth()
  const { rules } = useBusinessRulesContext()
  const { settings: shippingSettings } = useShipping()
  const outOfStockPolicy = rules?.outOfStockPolicy || { visibility: 'show', purchaseBehavior: 'block_purchase', inquiryChannel: 'whatsapp', labelText: 'Sin stock' }

  const [notification, setNotification] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})
  const [mercadoPagoRetryOrderId, setMercadoPagoRetryOrderId] = useState(null)
  const [fulfillmentSelection, setFulfillmentSelection] = useState({ method: 'delivery', pickupWarehouseId: '' })
  const enabledPaymentMethods = useMemo(() => {
    if (rules?.paymentMethods?.enabled === false) return []
    const methods = Array.isArray(rules?.paymentMethods?.methods) ? rules.paymentMethods.methods : []
    return methods.filter(m => m?.enabled !== false)
  }, [rules])

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('')

  useEffect(() => {
    if (!enabledPaymentMethods.length) {
      if (selectedPaymentMethodId) setSelectedPaymentMethodId('')
      return
    }
    const exists = enabledPaymentMethods.some(m => m.id === selectedPaymentMethodId)
    if (!selectedPaymentMethodId || !exists) setSelectedPaymentMethodId(enabledPaymentMethods[0].id)
  }, [enabledPaymentMethods, selectedPaymentMethodId])

  const selectedPaymentMethod = useMemo(() => {
    if (!selectedPaymentMethodId) return null
    return enabledPaymentMethods.find(m => m.id === selectedPaymentMethodId) || null
  }, [enabledPaymentMethods, selectedPaymentMethodId])

  const traceParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('trace') || params.get('debug')
    const enabled = raw === '1' || raw === 'true'
    const traceId = params.get('traceId') || params.get('trace_id')
    return { enabled, traceId: traceId || null }
  }, [])

  useEffect(() => {
    if (selectedPaymentMethod?.type !== 'gateway_mercadopago') {
      setMercadoPagoRetryOrderId(null)
    }
  }, [selectedPaymentMethod?.type])

  const buildOrderSnapshot = (source) => {
    if (!source) return null
    const total = Number(source.total)
    if (!Number.isFinite(total) || total <= 0) return null
    return {
      total,
      orderNumber: source.orderNumber || null,
      customerEmail: source.customer?.email || null,
      paymentType: source.payment?.type || null
    }
  }

  const buildGatewayPayload = (mp) => ({
    provider: 'mercadopago',
    preferenceId: mp?.preferenceId || null,
    initPoint: mp?.initPoint || null,
    sandboxInitPoint: mp?.sandboxInitPoint || null,
    status: 'created'
  })

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      province: '',
      country: 'ARGENTINA'
    },
    notes: ''
  })

  const preloadRef = useRef({ email: null })

  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/carrito')
    }
  }, [cartItems.length, navigate])

  useEffect(() => {
    const preload = async () => {
      if (!currentUser?.email) return
      const email = String(currentUser.email).trim().toLowerCase()
      if (!email) return
      if (preloadRef.current.email === email) return
      preloadRef.current.email = email

      setFormData(prev => ({
        ...prev,
        name: userData?.displayName || prev.name,
        email: email || prev.email,
        phone: userData?.phone || prev.phone
      }))
      try {
        const cacheKey = `pinohome_customer_profile:${email}`
        const cachedRaw = localStorage.getItem(cacheKey)
        if (cachedRaw) {
          try {
            const cached = JSON.parse(cachedRaw)
            const primaryPhone = cached?.phones?.length ? cached.phones[cached.primaryPhoneIndex || 0] : ''
            const primaryAddress = cached?.addresses?.length ? cached.addresses[cached.primaryAddressIndex || 0] : null
            setFormData(prev => ({
              ...prev,
              name: cached?.name || prev.name,
              email: email || prev.email,
              phone: primaryPhone || prev.phone,
              address: primaryAddress ? { ...prev.address, ...primaryAddress } : prev.address
            }))
          } catch (e) {
            console.error('Error leyendo cache de cliente:', e)
          }
        }

        const customer = await getCustomerProfileByEmail(email)
        const primaryPhone = customer?.phones?.length ? customer.phones[customer.primaryPhoneIndex || 0] : ''
        const primaryAddress = customer?.addresses?.length ? customer.addresses[customer.primaryAddressIndex || 0] : null
        setFormData(prev => ({
          ...prev,
          name: customer?.name || userData?.displayName || prev.name,
          email: email || prev.email,
          phone: primaryPhone || userData?.phone || prev.phone,
          address: primaryAddress ? { ...prev.address, ...primaryAddress } : prev.address
        }))
        if (customer) localStorage.setItem(cacheKey, JSON.stringify(customer))
      } catch {
        setFormData(prev => ({
          ...prev,
          name: userData?.displayName || prev.name,
          email: email || prev.email,
          phone: userData?.phone || prev.phone
        }))
      }
    }
    preload()
  }, [currentUser, userData])

  const baseSubtotal = getSubtotal()
  const shippingSettingsFulfillment = shippingSettings?.fulfillment || {}
  const negotiationEnabled = shippingSettings?.distancePricing?.negotiationEnabled === true || shippingSettings?.distancePricing?.negotiationEnabled === 'true'
  const availableFulfillmentMethods = useMemo(() => {
    const out = []
    if (shippingSettingsFulfillment.homeDeliveryEnabled !== false) out.push('delivery')
    if (shippingSettingsFulfillment.pickupEnabled === true) out.push('pickup')
    if (negotiationEnabled) out.push('negotiation')
    return out
  }, [negotiationEnabled, shippingSettingsFulfillment.homeDeliveryEnabled, shippingSettingsFulfillment.pickupEnabled])

  const { distance, status: distanceStatus, shouldComputeDistance, confirm: confirmDeliveryAddress, isConfirmed: isDeliveryConfirmed, canConfirm: canConfirmDeliveryAddress } = useDeliveryDistance({
    shippingSettings,
    address: formData.address,
    method: fulfillmentSelection.method
  })

  const shippingQuote = getShippingQuote(distance)
  const forcedMethod = shippingQuote.type === 'negotiation' ? 'negotiation' : null
  const effectiveMethod = forcedMethod || fulfillmentSelection.method
  const selectedMethod = fulfillmentSelection.method
  const isPickup = effectiveMethod === 'pickup'
  const isNegotiation = effectiveMethod === 'negotiation'
  const shipping = isPickup || isNegotiation || shippingQuote.type === 'pending' ? 0 : shippingQuote.cost
  const deliveryReadyForPayment = selectedMethod !== 'delivery'
    ? true
    : isDeliveryConfirmed && distanceStatus.state === 'ready' && shippingQuote.type !== 'pending' && shippingQuote.type !== 'disabled'
  const paymentLocked = selectedMethod === 'delivery' && !deliveryReadyForPayment
  const canProceedToPay = !submitting && (
    effectiveMethod === 'pickup'
      ? !!fulfillmentSelection.pickupWarehouseId
      : selectedMethod === 'delivery'
        ? deliveryReadyForPayment
        : true
  )

  const calculatePaymentAdjustment = (subtotalValue, priceAdjustment) => {
    if (!priceAdjustment) return 0
    const rawValue = Number(priceAdjustment.value)
    if (!Number.isFinite(rawValue) || rawValue === 0) return 0
    const baseAmount = priceAdjustment.kind === 'fixed' ? rawValue : (subtotalValue * rawValue) / 100
    const sign = priceAdjustment.sign === 'surcharge' ? 1 : -1
    return baseAmount * sign
  }

  const paymentAdjustmentAmount = selectedPaymentMethod?.priceAdjustment
    ? calculatePaymentAdjustment(baseSubtotal, selectedPaymentMethod.priceAdjustment)
    : 0

  const subtotal = Math.max(0, baseSubtotal + paymentAdjustmentAmount)
  const total = subtotal + shipping

  const validate = () => {
    const next = {}
    if (!formData.name.trim()) next.name = 'El nombre es requerido'
    if (!formData.email.trim()) next.email = 'El email es requerido'
    else if (!isValidEmail(formData.email)) next.email = 'El email no es válido'
    if (!formData.phone.trim()) next.phone = 'El teléfono es requerido'
    if (effectiveMethod === 'pickup' && !fulfillmentSelection.pickupWarehouseId) {
      next.fulfillment = 'Selecciona una sucursal para retirar'
    }
    if (availableFulfillmentMethods.length === 0) {
      next.fulfillment = 'No hay métodos de entrega disponibles'
    } else if (shippingQuote.type === 'disabled') {
      next.fulfillment = 'Los envíos están deshabilitados en la configuración'
    }
    if (selectedMethod === 'delivery') {
      const street = String(formData.address?.street || '').trim()
      const city = String(formData.address?.city || '').trim()
      const province = String(formData.address?.province || '').trim()
      const country = String(formData.address?.country || '').trim()
      if (!street) next.street = 'La dirección es obligatoria para envío a domicilio'
      if (!city) next.city = 'La ciudad es obligatoria para envío a domicilio'
      if (!province) next.province = 'La provincia es obligatoria para envío a domicilio'
      if (!country) next.country = 'El país es obligatorio para envío a domicilio'
      if (!isDeliveryConfirmed) {
        next.address = 'Confirmá tu dirección de entrega para calcular el envío'
      } else if (distanceStatus.state === 'error') {
        next.address = distanceStatus.message || 'No se pudo calcular el envío'
      } else if (shouldComputeDistance && street && city && province && distanceStatus.state !== 'ready') {
        next.address = distanceStatus.message || 'Estamos calculando tu costo de envío…'
      }
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name.startsWith('address.')) {
      const k = name.split('.')[1]
      const nextValue = k === 'country' ? normalizeCountryName(value) : value
      setFormData(prev => ({ ...prev, address: { ...prev.address, [k]: nextValue } }))
      return
    }
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitting) return
    if (!validate()) return

    if (outOfStockPolicy.purchaseBehavior !== 'allow_purchase') {
      const summaries = new Map()
      const hasVariantBreakdown = (summary) => {
        const byColor = summary?.byColor || {}
        const bySize = summary?.bySize || {}
        const byColorSize = summary?.byColorSize || {}
        return Object.keys(byColor).length > 0 || Object.keys(bySize).length > 0 || Object.keys(byColorSize).length > 0
      }
      const getSummary = async (productId) => {
        if (!productId) return null
        if (summaries.has(productId)) return summaries.get(productId)
        try {
          const summary = await getProductStockSummary(productId)
          summaries.set(productId, summary)
          return summary
        } catch {
          summaries.set(productId, null)
          return null
        }
      }

      const liveStocks = await Promise.all(
        cartItems.map(async (item) => {
          const variant = item?.variant
          const selection = variant?.type === 'fashion' ? variant?.selection : null
          if (selection && (selection.color || selection.size)) {
            const summary = await getSummary(item.productId)
            if (summary) {
              const stock = hasVariantBreakdown(summary)
                ? getStockForSelection(summary, { color: selection.color, size: selection.size })
                : Number(summary.total || 0) || 0
              return { id: item.id, stock }
            }
          }
          return { id: item.id, stock: Number(item?.stock || 0) || 0 }
        })
      )

      const stockMap = new Map(liveStocks.map(s => [s.id, s.stock]))
      const invalid = cartItems.filter(i => {
        const stock = stockMap.get(i.id) ?? (i?.stock || 0)
        return stock <= 0 || (i?.quantity || 0) > stock
      })
      if (invalid.length > 0) {
        const message = outOfStockPolicy.purchaseBehavior === 'inquiry_only'
          ? 'Hay productos sin stock. Este checkout admite solo consulta.'
          : 'Hay productos sin stock o con cantidad mayor al stock.'
        setNotification({ type: 'error', message })
        setTimeout(() => setNotification(null), 5000)
        if (outOfStockPolicy.purchaseBehavior === 'inquiry_only') {
          navigate(outOfStockPolicy.inquiryChannel === 'help_page' ? '/ayuda' : '/ayuda#contacto')
        }
        return
      }
    }

    const isMercadoPago = selectedPaymentMethod?.type === 'gateway_mercadopago'
    if (isMercadoPago && mercadoPagoRetryOrderId) {
      setSubmitting(true)
      try {
        const existingOrder = await getOrder(mercadoPagoRetryOrderId).catch(() => null)
        const orderSnapshot = buildOrderSnapshot(existingOrder)
        const mp = await createMercadoPagoPreference({
          orderId: mercadoPagoRetryOrderId,
          storefrontUrl: window.location.origin,
          orderSnapshot,
          trace: traceParams.enabled,
          traceId: traceParams.traceId
        })
        const gatewayPayload = buildGatewayPayload(mp)
        await updateOrder(mercadoPagoRetryOrderId, { paymentGateway: gatewayPayload }).catch(() => null)
        const redirectUrl = mp?.redirectUrl || mp?.sandboxInitPoint || mp?.initPoint
        if (!redirectUrl) {
          throw new Error('MercadoPago no devolvió un link de pago')
        }
        clearCart()
        window.location.href = redirectUrl
        return
      } catch (e) {
        setNotification({
          type: 'error',
          message: `No se pudo generar el link de MercadoPago: ${e?.message || 'Error desconocido'}`
        })
        setTimeout(() => setNotification(null), 6000)
        setSubmitting(false)
        return
      }
    }

    setMercadoPagoRetryOrderId(null)
    setSubmitting(true)
    try {
      const orderData = {
        customer: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          address: {
            street: formData.address.street?.trim() || '',
            city: formData.address.city?.trim() || '',
            postalCode: formData.address.postalCode?.trim() || '',
            province: formData.address.province?.trim() || '',
            country: formData.address.country || 'ARGENTINA'
          }
        },
        items: cartItems.map(item => ({
          productId: item.productId,
          productName: item.productName,
          sku: item.sku,
          variant: item.variant,
          price: item.price,
          quantity: item.quantity,
          image: item.image || null
        })),
        subtotal,
        shipping,
        total,
        notes: formData.notes.trim(),
        fulfillment: effectiveMethod === 'pickup'
          ? { method: 'pickup', pickupWarehouseId: fulfillmentSelection.pickupWarehouseId }
          : effectiveMethod === 'negotiation'
            ? { method: 'negotiation' }
            : { method: 'delivery' },
        payment: selectedPaymentMethod ? {
          methodId: selectedPaymentMethod.id,
          type: selectedPaymentMethod.type,
          label: selectedPaymentMethod.label,
          customerNote: selectedPaymentMethod.customerNote || '',
          priceAdjustment: selectedPaymentMethod.priceAdjustment || null,
          selectedAt: new Date()
        } : null,
        paymentAdjustment: selectedPaymentMethod?.priceAdjustment && paymentAdjustmentAmount
          ? { ...selectedPaymentMethod.priceAdjustment, amount: paymentAdjustmentAmount }
          : null,
        distance
      }

      const checkoutResponse = await createCheckoutOrder({
        orderData,
        storefrontUrl: window.location.origin,
        trace: traceParams.enabled,
        traceId: traceParams.traceId
      })
      const orderId = checkoutResponse?.orderId
      if (orderId) {
        notifyOrderCreated(orderId).catch(() => null)
      }

      if (isMercadoPago) {
        const redirectUrl = checkoutResponse?.redirectUrl
        if (!redirectUrl) {
          throw new Error('MercadoPago no devolvió un link de pago')
        }
        clearCart()
        setMercadoPagoRetryOrderId(null)
        window.location.href = redirectUrl
        return
      }

      clearCart()
      setMercadoPagoRetryOrderId(null)
      navigate(`/checkout/confirmacion/${orderId}`)
    } catch (e) {
      const fallbackOrderId = e?.data?.orderId || null
      setNotification({
        type: 'error',
        message: `No se pudo generar el link de MercadoPago: ${e?.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 6000)
      if (isMercadoPago && fallbackOrderId) {
        clearCart()
        setMercadoPagoRetryOrderId(fallbackOrderId)
      }
      setSubmitting(false)
      return
    }
  }

  const handleMercadoPagoRetry = async () => {
    if (!mercadoPagoRetryOrderId || submitting) return
    setSubmitting(true)
    try {
      const existingOrder = await getOrder(mercadoPagoRetryOrderId).catch(() => null)
      const orderSnapshot = buildOrderSnapshot(existingOrder)
      const mp = await createMercadoPagoPreference({
        orderId: mercadoPagoRetryOrderId,
        storefrontUrl: window.location.origin,
        orderSnapshot,
        trace: traceParams.enabled,
        traceId: traceParams.traceId
      })
      const gatewayPayload = buildGatewayPayload(mp)
      await updateOrder(mercadoPagoRetryOrderId, { paymentGateway: gatewayPayload }).catch(() => null)
      const redirectUrl = mp?.redirectUrl || mp?.sandboxInitPoint || mp?.initPoint
      if (!redirectUrl) {
        throw new Error('MercadoPago no devolvió un link de pago')
      }
      clearCart()
      window.location.href = redirectUrl
    } catch (e) {
      setNotification({
        type: 'error',
        message: `No se pudo generar el link de MercadoPago: ${e?.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 6000)
      setSubmitting(false)
    }
  }

  if (cartItems.length === 0) {
    return null
  }

  return (
    <main className="relative max-w-[1200px] mx-auto px-6 py-12">
      {submitting && selectedPaymentMethod?.type === 'gateway_mercadopago' && (
        <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-2xl px-8 py-6 shadow-xl max-w-sm w-[90%] text-center">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full border-4 border-[#c27e70] border-t-transparent animate-spin" />
            <p className="text-lg font-semibold text-[#181211] mb-1">
              Redirigiendo a MercadoPago...
            </p>
            <p className="text-sm text-gray-600">
              Por favor no cierres esta ventana ni recargues la página.
            </p>
          </div>
        </div>
      )}
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 space-y-12">
          <form onSubmit={handleSubmit} className="space-y-12">
            <section>
              <div className="flex items-center gap-4 mb-8">
                <span className="size-8 rounded-full bg-[#c27e70] text-white flex items-center justify-center font-bold text-sm">1</span>
                <h2 className="text-2xl font-bold tracking-tight">Información de Envío</h2>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Nombre Completo</label>
                    <input
                      className={`w-full px-4 py-3 bg-white border rounded-lg outline-none transition-all text-sm focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70] ${errors.name ? 'border-red-500' : 'border-[#e8e6e1]'}`}
                      placeholder="Ej. Ana García"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                    />
                    {errors.name ? <p className="text-xs text-red-600 font-semibold">{errors.name}</p> : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Correo Electrónico</label>
                    <input
                      className={`w-full px-4 py-3 bg-white border rounded-lg outline-none transition-all text-sm focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70] ${errors.email ? 'border-red-500' : 'border-[#e8e6e1]'}`}
                      placeholder="ana@ejemplo.com"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                    {errors.email ? <p className="text-xs text-red-600 font-semibold">{errors.email}</p> : null}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Teléfono</label>
                  <input
                    className={`w-full px-4 py-3 bg-white border rounded-lg outline-none transition-all text-sm focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70] ${errors.phone ? 'border-red-500' : 'border-[#e8e6e1]'}`}
                    placeholder="+54 11 0000 0000"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                  {errors.phone ? <p className="text-xs text-red-600 font-semibold">{errors.phone}</p> : null}
                </div>

                {shippingSettings?.fulfillment && (
                  <div className="space-y-2">
                    <FulfillmentSelector
                      shippingSettings={shippingSettings}
                      value={fulfillmentSelection}
                      onChange={(next) => {
                        setFulfillmentSelection(next)
                        if (errors.fulfillment) setErrors(prev => ({ ...prev, fulfillment: '' }))
                      }}
                      disabled={submitting}
                      availableMethods={availableFulfillmentMethods}
                      forcedMethod={forcedMethod || undefined}
                    />
                    {errors.fulfillment ? <p className="text-xs text-red-600 font-semibold">{errors.fulfillment}</p> : null}
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">
                    Dirección <span className="text-[11px] font-normal normal-case text-green-600">{selectedMethod === 'pickup' ? '(No requerida para retiro)' : selectedMethod === 'negotiation' ? '(Opcional para acordar el envío)' : '(Obligatoria)'}</span>
                  </label>
                  <p className="text-xs text-gray-400 -mt-1">{selectedMethod === 'pickup' ? 'Elegiste retiro por sucursal. No es necesario cargar una dirección.' : selectedMethod === 'negotiation' ? 'Acordarás el envío con el vendedor. Podés dejar la dirección vacía.' : 'La dirección es necesaria para calcular el costo de envío.'}</p>
                  {errors.address ? <p className="text-xs text-red-600 font-semibold">{errors.address}</p> : null}
                  <input
                    className={`w-full px-4 py-3 bg-white border rounded-lg outline-none transition-all text-sm focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70] ${errors.street ? 'border-red-500' : 'border-[#e8e6e1]'}`}
                    placeholder="Calle, número, piso..."
                    type="text"
                    name="address.street"
                    value={formData.address.street}
                    onChange={handleChange}
                  />
                  {errors.street ? <p className="text-xs text-red-600 font-semibold">{errors.street}</p> : null}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Ciudad</label>
                    <input
                      className={`w-full px-4 py-3 bg-white border rounded-lg outline-none transition-all text-sm focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70] ${errors.city ? 'border-red-500' : 'border-[#e8e6e1]'}`}
                      placeholder="Buenos Aires"
                      type="text"
                      name="address.city"
                      value={formData.address.city}
                      onChange={handleChange}
                    />
                    {errors.city ? <p className="text-xs text-red-600 font-semibold">{errors.city}</p> : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Código Postal</label>
                    <input
                      className={`w-full px-4 py-3 bg-white border rounded-lg outline-none transition-all text-sm focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70] ${errors.postalCode ? 'border-red-500' : 'border-[#e8e6e1]'}`}
                      placeholder="C1043"
                      type="text"
                      name="address.postalCode"
                      value={formData.address.postalCode}
                      onChange={handleChange}
                    />
                    {errors.postalCode ? <p className="text-xs text-red-600 font-semibold">{errors.postalCode}</p> : null}
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Provincia</label>
                    <input
                      className={`w-full px-4 py-3 bg-white border rounded-lg outline-none transition-all text-sm focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70] ${errors.province ? 'border-red-500' : 'border-[#e8e6e1]'}`}
                      placeholder="CABA"
                      type="text"
                      name="address.province"
                      value={formData.address.province}
                      onChange={handleChange}
                    />
                    {errors.province ? <p className="text-xs text-red-600 font-semibold">{errors.province}</p> : null}
                  </div>
                </div>

                <div className="space-y-2 mt-4">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">País</label>
                  <input
                    className={`w-full px-4 py-3 bg-white border rounded-lg outline-none transition-all text-sm focus:ring-1 focus:ring-[#c27e70] focus:border-[#c27e70] ${errors.country ? 'border-red-500' : 'border-[#e8e6e1]'}`}
                    placeholder="ARGENTINA"
                    type="text"
                    name="address.country"
                    value={formData.address.country || 'ARGENTINA'}
                    onChange={handleChange}
                  />
                  {errors.country ? <p className="text-xs text-red-600 font-semibold">{errors.country}</p> : null}
                </div>

                {selectedMethod === 'delivery' ? (
                  <div className="mt-4 flex flex-col gap-2">
                    <button
                      type="button"
                      className="w-full bg-white border border-[#e8e6e1] hover:border-[#c27e70] hover:text-[#c27e70] transition-colors rounded-lg py-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                      onClick={() => confirmDeliveryAddress()}
                      disabled={submitting || !canConfirmDeliveryAddress || distanceStatus.state === 'loading'}
                    >
                      <span className="material-symbols-outlined text-[18px]">location_on</span>
                      {distanceStatus.state === 'loading'
                        ? 'Calculando envío...'
                        : isDeliveryConfirmed && distanceStatus.state === 'ready'
                          ? 'Dirección confirmada'
                          : 'Confirmar dirección de entrega'}
                    </button>
                    {distanceStatus.message ? (
                      <div className={`text-xs ${distanceStatus.state === 'error' ? 'text-red-600' : 'text-gray-500'}`}>
                        {distanceStatus.message}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </section>

            <hr className="border-[#e8e6e1]" />

            <section>
              <div className="flex items-center gap-4 mb-8">
                <span className="size-8 rounded-full bg-[#e8e6e1] text-gray-600 flex items-center justify-center font-bold text-sm">2</span>
                <h2 className="text-2xl font-bold tracking-tight">Método de Pago</h2>
              </div>

              {enabledPaymentMethods.length === 0 ? (
                <div className="text-sm text-gray-500">No hay medios de pago configurados.</div>
              ) : (
                <PaymentMethodSelector
                  methods={enabledPaymentMethods}
                  selectedMethodId={selectedPaymentMethodId}
                  onSelectMethodId={setSelectedPaymentMethodId}
                  disabled={paymentLocked}
                  theme={{
                    accent: '#c27e70',
                    accentSoft: '#fff7f5',
                    text: '#181211',
                    muted: '#6b7280',
                    border: '#e8e6e1',
                    surface: '#ffffff',
                    surfaceMuted: '#f8f7f6'
                  }}
                  onNotify={(n) => setNotification(n)}
                  formatCurrency={formatCurrency}
                />
              )}
            </section>

            <div className="pt-8 flex flex-col items-center">
              <button
                type="submit"
                className="w-full md:w-auto min-w-[300px] bg-[#c27e70] hover:bg-[#a66a5e] text-white px-12 py-5 rounded-full font-extrabold text-base tracking-wide transition-all transform hover:scale-[1.02] active:scale-95 shadow-xl shadow-[#c27e70]/30 disabled:opacity-60"
                disabled={!canProceedToPay}
              >
                {submitting ? 'Procesando...' : paymentLocked ? 'Confirmá tu dirección' : 'Pagar Ahora'}
              </button>
              {selectedPaymentMethod?.type === 'gateway_mercadopago' && mercadoPagoRetryOrderId && (
                <button
                  type="button"
                  onClick={handleMercadoPagoRetry}
                  disabled={submitting}
                  className="w-full md:w-auto min-w-[300px] mt-3 bg-[#c27e70]/80 hover:bg-[#a66a5e] text-white px-12 py-4 rounded-full font-extrabold text-base tracking-wide transition-all transform hover:scale-[1.02] active:scale-95 shadow-xl shadow-[#c27e70]/30 disabled:opacity-60"
                >
                  Reintentar MercadoPago
                </button>
              )}
              <p className="mt-4 text-xs text-gray-400 flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">verified_user</span>
                Transacción cifrada de 256 bits
              </p>
            </div>
          </form>
        </div>

        <div className="lg:col-span-5">
          <div className="sticky top-28 bg-white rounded-2xl border border-[#e8e6e1] p-8 shadow-sm">
            <h3 className="text-xl font-bold mb-8">Resumen de tu Pedido</h3>
            <div className="space-y-6 mb-8">
              {cartItems.map((item) => {
                const img = getImageUrl(item.image, { width: 240, height: 240, crop: 'fill' })
                return (
                  <div key={item.id} className="flex gap-4">
                    <div className="size-20 bg-[#f4f3f1] rounded-lg overflow-hidden flex-shrink-0">
                      <img alt={item.productName} className="w-full h-full object-cover" src={img} />
                    </div>
                    <div className="flex flex-col justify-center flex-1">
                      <h4 className="font-bold text-sm">{item.productName}</h4>
                      {item.variant?.name ? (
                        <p className="text-xs text-gray-500">{item.variant.name}</p>
                      ) : null}
                      <p className="font-bold text-sm mt-1">
                        {formatCurrency(item.price || 0)} <span className="text-gray-400 font-medium">x{item.quantity}</span>
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="space-y-3 border-t border-[#f1efee] pt-6">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal</span>
                <span>{formatCurrency(baseSubtotal)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Envío</span>
                <span className="text-[#c27e70] font-medium">
                  {isPickup || isNegotiation
                    ? (isNegotiation ? 'A acordar' : 'Gratis')
                    : shippingQuote.type === 'disabled'
                      ? 'No disponible'
                    : shippingQuote.type === 'pending'
                      ? (distanceStatus.message || 'Estamos calculando tu costo de envío…')
                      : (shippingQuote.type === 'free' && shipping === 0 ? 'Gratis' : formatCurrency(shipping))}
                </span>
              </div>
              {selectedPaymentMethod?.label ? (
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Medio de pago</span>
                  <span>{selectedPaymentMethod.label}</span>
                </div>
              ) : null}
              {paymentAdjustmentAmount !== 0 ? (
                <div className="flex justify-between text-sm text-gray-600">
                  <span>{paymentAdjustmentAmount < 0 ? 'Descuento' : 'Recargo'}</span>
                  <span className={`${paymentAdjustmentAmount < 0 ? 'text-green-600' : 'text-orange-600'} font-semibold`}>
                    {paymentAdjustmentAmount < 0 ? '-' : '+'}{formatCurrency(Math.abs(paymentAdjustmentAmount))}
                  </span>
                </div>
              ) : null}
              <div className="flex justify-between text-sm text-gray-600">
                <span>Impuestos</span>
                <span>{formatCurrency(0)}</span>
              </div>
              <div className="flex justify-between text-xl font-extrabold pt-4 border-t border-[#f1efee]">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-center gap-6 opacity-40 grayscale">
              <span className="text-xs font-bold">VISA</span>
              <span className="text-xs font-bold">MASTERCARD</span>
              <span className="text-xs font-bold">PAYPAL</span>
            </div>

            <div className="mt-8">
              <Link className="text-sm font-semibold text-gray-500 hover:text-[#c27e70] transition-colors" to="/carrito">
                Volver al carrito
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}

export default FashionCheckout
